import React from 'react';
import { ArrowLeft, Terminal } from 'lucide-react';
import { CodeBlock } from './CodeBlock';
import {
  docEndpointBySlug,
  docSectionById,
  slugForEndpoint,
  methodColor,
  authLabel,
  type DocEndpoint,
  type DocSection,
} from './docsData';

// EndpointDoc renders the standalone reference page for a single endpoint at
// /docs/{slug}. It reuses the parent section's intro, request params, and example
// so each endpoint is independently crawlable (own <title>, listed in the sitemap)
// without duplicating prose. Linked from the endpoint list on /docs.

const ORIGIN = 'https://app.nz';

// buildCurl produces a copy-pasteable request for this exact endpoint.
function buildCurl(e: DocEndpoint): string {
  const url = ORIGIN + e.path;
  const authHeader = e.auth === 'none' ? '' : ` \\\n  -H "Authorization: Bearer pk_live_..."`;
  if (e.method === 'GET' || e.method === 'DELETE') {
    const flag = e.method === 'GET' ? '-s' : '-sX DELETE';
    return `curl ${flag} "${url}"${authHeader}`;
  }
  return (
    `curl -sX ${e.method} ${url}${authHeader} \\\n` +
    `  -H "Content-Type: application/json" \\\n` +
    `  -d '{}'`
  );
}

const NotFound: React.FC<{ slug: string }> = ({ slug }) => (
  <main className="min-h-screen bg-black px-4 pb-16 pt-24 text-white sm:px-6 lg:px-8">
    <div className="mx-auto max-w-2xl">
      <a href="/docs" className="mb-5 inline-flex items-center gap-2 text-sm font-semibold text-white/48 hover:text-white">
        <ArrowLeft size={16} />
        API reference
      </a>
      <h1 className="font-display text-2xl font-bold">Unknown endpoint</h1>
      <p className="mt-3 text-sm leading-7 text-white/58">
        There is no documented endpoint at <code className="text-white/80">/docs/{slug}</code>. Browse the full{' '}
        <a href="/docs" className="text-white underline underline-offset-4">API reference</a>.
      </p>
    </div>
  </main>
);

export const EndpointDoc: React.FC<{ slug: string }> = ({ slug }) => {
  const endpoint = docEndpointBySlug(slug);
  if (!endpoint) return <NotFound slug={slug} />;
  const section = docSectionById(endpoint.sectionId) as DocSection;
  const siblings = (section.endpoints ?? []).filter(
    (e) => !(e.method === endpoint.method && e.path === endpoint.path) && !e.noDoc,
  );

  return (
    <main className="min-h-screen bg-black px-4 pb-16 pt-24 text-white sm:px-6 lg:px-8">
      <div className="mx-auto max-w-3xl min-w-0">
        <nav className="mb-5 flex flex-wrap items-center gap-2 text-sm font-semibold text-white/48">
          <a href="/docs" className="inline-flex items-center gap-2 hover:text-white">
            <ArrowLeft size={16} />
            API reference
          </a>
          <span className="text-white/24">/</span>
          <a href={`/docs/${section.id}`} className="hover:text-white">
            {section.title}
          </a>
        </nav>

        <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-white/54">
          <Terminal size={14} />
          endpoint
        </div>

        <div className="mb-4 flex flex-wrap items-center gap-3">
          <span
            className={`inline-flex shrink-0 justify-center rounded border px-2.5 py-1 text-sm font-bold ${methodColor[endpoint.method]}`}
          >
            {endpoint.method}
          </span>
          <h1 className="min-w-0 break-all font-mono text-xl font-bold sm:text-2xl">{endpoint.path}</h1>
        </div>

        <p className="mb-5 text-base leading-7 text-white/72">{endpoint.summary}</p>

        <div className="mb-6 flex flex-wrap gap-2">
          {endpoint.auth && (
            <span className="rounded border border-white/10 bg-white/[0.04] px-2 py-1 text-xs font-semibold uppercase tracking-wide text-white/54">
              auth: {authLabel[endpoint.auth]}
            </span>
          )}
          {section.baseUrl && (
            <span className="rounded border border-white/10 bg-white/[0.04] px-2 py-1 font-mono text-xs text-white/54">
              base {section.baseUrl}
            </span>
          )}
        </div>

        <div className="mb-6">
          <div className="mb-2 text-xs font-bold uppercase tracking-[0.16em] text-white/34">Request</div>
          <CodeBlock code={buildCurl(endpoint)} language="bash" preClassName="!text-[13px] !leading-6" />
        </div>

        <div className="mb-6 rounded-xl border border-white/10 bg-white/[0.035] p-5">
          <div className="mb-2 flex items-center gap-3">
            <section.icon size={18} className="text-white/54" />
            <h2 className="font-display text-lg font-bold">{section.title}</h2>
          </div>
          <p className="text-sm leading-7 text-white/58">{section.intro}</p>
        </div>

        {section.params && (
          <div className="mb-6 overflow-hidden rounded-xl border border-white/10">
            <div className="border-b border-white/10 bg-white/[0.04] px-3 py-2 text-xs font-bold uppercase tracking-[0.16em] text-white/40">
              {section.params.heading}
            </div>
            <div className="divide-y divide-white/[0.06]">
              {section.params.rows.map((p) => (
                <div key={p.name} className="grid gap-1 px-3 py-2 sm:grid-cols-[0.28fr_0.14fr_0.58fr] sm:items-baseline">
                  <code className="font-mono text-sm text-white/80">
                    {p.name}
                    {p.required && <span className="ml-1 text-rose-300/80">*</span>}
                  </code>
                  <span className="font-mono text-xs text-white/40">{p.type}</span>
                  <span className="text-sm text-white/54">{p.desc}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {section.example && (
          <div className="mb-6">
            <div className="mb-2 text-xs font-bold uppercase tracking-[0.16em] text-white/34">{section.example.label}</div>
            <CodeBlock
              code={section.example.code}
              language={/^\s*curl\b/m.test(section.example.code) || /^\s*(#|app )/.test(section.example.code) ? 'bash' : undefined}
              preClassName="!text-[13px] !leading-6"
            />
          </div>
        )}

        {siblings.length > 0 && (
          <div className="rounded-xl border border-white/10 bg-white/[0.035] p-5">
            <div className="mb-3 text-xs font-bold uppercase tracking-[0.16em] text-white/40">
              More in {section.title}
            </div>
            <div className="grid gap-2">
              {siblings.map((e) => (
                <a
                  key={e.method + e.path}
                  href={`/docs/${slugForEndpoint(e.method, e.path) ?? ''}`}
                  className="flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-lg border border-white/10 bg-black px-3 py-2 transition-colors hover:border-white/25 hover:bg-white/[0.04]"
                >
                  <span
                    className={`inline-flex w-16 shrink-0 justify-center rounded border px-2 py-0.5 text-xs font-bold ${methodColor[e.method]}`}
                  >
                    {e.method}
                  </span>
                  <code className="min-w-0 break-all font-mono text-sm text-white/80">{e.path}</code>
                  <span className="min-w-0 break-words text-sm text-white/48 sm:ml-auto">{e.summary}</span>
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
    </main>
  );
};
